import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Download, FileDown, User, FileText, Loader2, Shield, AlertTriangle, ArrowRight, Pencil } from 'lucide-react';
import useDossierStore from '@/stores/useDossierStore';
import useAuthStore from '@/stores/useAuthStore';
import { formatDate, statusLabel } from '@/lib/utils';
import { rapportsApi, dossiersApi } from '@/api';
import { DOSSIER_TRANSITIONS } from '@/constants/enums';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import RapportVersionList from '@/components/rapport/RapportVersionList';
import PhaseProgressBar from '@/components/dossier/PhaseProgressBar';
import ActivityTimeline from '@/components/dossier/ActivityTimeline';
import AssignExpertModal from '@/components/dossier/AssignExpertModal';

const SECTION_LABELS = {
  mission: 'Mission',
  designation: 'Désignation',
  description: 'Description',
  juridique: 'Analyse juridique',
  marche: 'Étude de marché',
  valorisation: 'Estimation de la valeur',
  photos: 'Photos',
};

function groupMissingFieldsBySection(missing) {
  const groups = new Map();
  for (const entry of missing) {
    const key = entry.section || 'autre';
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(entry);
  }
  return groups;
}

export default function DossierDetailPage() {
  const { id } = useParams();
  const { currentDossier, loading, fetchDossier } = useDossierStore();
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);
  const [missingFields, setMissingFields] = useState(null);
  const [changingStatut, setChangingStatut] = useState(false);
  const [versionRefreshKey, setVersionRefreshKey] = useState(0);
  const [phaseProgress, setPhaseProgress] = useState(null);
  const [activities, setActivities] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const { user } = useAuthStore();

  const canAssignExpert =
    !!currentDossier &&
    (user?.role === 'admin' || currentDossier.expert_id === user?.id);

  const expertLabel = currentDossier?.expert
    ? `${currentDossier.expert.prenom} ${currentDossier.expert.nom}`
    : 'Non assigne';

  useEffect(() => {
    fetchDossier(id);
  }, [id, fetchDossier]);

  // Fetch phase progress
  useEffect(() => {
    if (!id) return;
    dossiersApi
      .getPhaseProgress(id)
      .then((res) => setPhaseProgress(res.phases))
      .catch(() => {});
  }, [id, currentDossier?.phase, currentDossier?.phases_validees]);

  // Fetch recent activities
  useEffect(() => {
    if (!id) return;
    dossiersApi
      .getActivities(id)
      .then((res) => setActivities(res))
      .catch(() => setActivities([]));
  }, [id, versionRefreshKey, currentDossier?.statut, currentDossier?.phase]);

  const handleChangeStatut = async (newStatut) => {
    if (newStatut === 'envoye' && !window.confirm('Marquer ce dossier comme envoyé ? Cette action est irréversible.')) {
      return;
    }
    setChangingStatut(true);
    try {
      await dossiersApi.changeStatut(id, newStatut);
      fetchDossier(id);
    } catch (err) {
      setGenError(err.message || 'Erreur lors du changement de statut');
    } finally {
      setChangingStatut(false);
    }
  };

  const handleGenerate = async (format) => {
    setGenerating(true);
    setGenError(null);
    setMissingFields(null);
    try {
      const version = await rapportsApi.generer(id, format);
      setVersionRefreshKey((k) => k + 1);
      await rapportsApi.telecharger(version.id);
    } catch (err) {
      const structured = err?.detail?.detail;
      if (err?.status === 422 && structured?.code === 'dossier_incomplet' && Array.isArray(structured.missing)) {
        setMissingFields(structured.missing);
        setGenError(structured.message || 'Le dossier est incomplet.');
      } else {
        setGenError(err?.message || 'Erreur lors de la génération du rapport');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-slate-600">Chargement du dossier...</p>
      </div>
    );
  }

  if (!currentDossier) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Dossier non trouvé</h2>
          <p className="text-slate-600 mb-4">Le dossier demandé n&apos;existe pas ou a été supprimé.</p>
          <Link to="/dossiers">
            <Button variant="primary">Retour à la liste</Button>
          </Link>
        </div>
      </div>
    );
  }

  const expertise = currentDossier.expertise;
  const firstParcel = expertise?.parcelles?.[0];
  const adresse = firstParcel
    ? `${firstParcel.adresse || ''}, ${firstParcel.code_postal || ''} ${firstParcel.commune || ''}`
    : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-900">
              {currentDossier.reference}
            </h1>
            <Badge variant={currentDossier.statut}>
              {statusLabel(currentDossier.statut)}
            </Badge>
          </div>
          <p className="text-sm text-slate-500">
            {currentDossier.client?.prenom} {currentDossier.client?.nom}
            {adresse && ` — ${adresse}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/dossiers/${id}/expertise`}>
            <Button variant="primary">Ouvrir l&apos;expertise</Button>
          </Link>
          {/* Status transitions */}
          {DOSSIER_TRANSITIONS[currentDossier.statut]?.map((t) => (
            <Button
              key={t.value}
              variant={t.variant || 'secondary'}
              size="sm"
              onClick={() => handleChangeStatut(t.value)}
              disabled={changingStatut}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Generation Error */}
      {genError && !missingFields && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm">{genError}</p>
        </div>
      )}

      {/* Missing fields panel (structured 422 from /rapports/generer) */}
      {missingFields && missingFields.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-amber-900 mb-1">
                Le dossier est incomplet
              </h3>
              <p className="text-sm text-amber-800">
                {genError || 'Complétez les champs suivants avant de générer le rapport :'}
              </p>
            </div>
          </div>
          <div className="space-y-3 pl-8">
            {Array.from(groupMissingFieldsBySection(missingFields).entries()).map(([section, entries]) => (
              <div key={section} className="bg-white/60 rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-amber-900 uppercase tracking-wide">
                    {SECTION_LABELS[section] || section}
                  </h4>
                  <Link
                    to={`/dossiers/${id}/expertise?section=${encodeURIComponent(section)}`}
                    className="text-xs font-medium text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                  >
                    Compléter
                    <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <ul className="space-y-1">
                  {entries.map((entry) => (
                    <li key={entry.field} className="text-sm text-amber-900 list-disc list-inside">
                      {entry.label}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Client */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <User className="w-4 h-4 text-slate-400" />
            <h3 className="text-[10px] font-semibold text-slate-400 uppercase">
              Client
            </h3>
          </div>
          <p className="text-sm font-semibold text-slate-900">
            {currentDossier.client?.prenom} {currentDossier.client?.nom}
          </p>
          {currentDossier.client?.email && (
            <p className="text-xs text-slate-500 mt-0.5">
              {currentDossier.client.email}
            </p>
          )}
        </div>

        {/* Mission */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-slate-400" />
            <h3 className="text-[10px] font-semibold text-slate-400 uppercase">
              Mission
            </h3>
          </div>
          <p className="text-sm font-semibold text-slate-900">
            {expertise?.type_valeur === 'venale'
              ? 'Expertise vénale'
              : expertise?.type_valeur === 'locative'
                ? 'Expertise locative'
                : 'Expertise vénale et locative'}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            Usage {currentDossier.usage_rapport} — Mandat{' '}
            {formatDate(currentDossier.date_mandat)}
          </p>
        </div>

        {/* Expert */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 relative">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-400" />
              <h3 className="text-[10px] font-semibold text-slate-400 uppercase">
                Expert
              </h3>
            </div>
            {canAssignExpert && (
              <button
                type="button"
                onClick={() => setAssignOpen(true)}
                className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition"
                title="Changer l'expert"
                aria-label="Changer l'expert"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <p className="text-sm font-semibold text-slate-900">{expertLabel}</p>
          {currentDossier.expert?.role && (
            <p className="text-xs text-slate-500 mt-0.5 capitalize">
              {currentDossier.expert.role.replace('_', ' ')}
            </p>
          )}
        </div>
      </div>

      {/* Phase Progress */}
      <PhaseProgressBar
        dossier={currentDossier}
        phaseProgress={phaseProgress}
      />

      {/* Bottom: Rapport + Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Rapport */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">Rapport</h2>

          <RapportVersionList dossierId={id} refreshKey={versionRefreshKey} />

          <div className="flex gap-2 mt-4">
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
              onClick={() => handleGenerate('docx')}
              disabled={generating}
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Générer DOCX
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="flex-1"
              onClick={() => handleGenerate('pdf')}
              disabled={generating}
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileDown className="w-4 h-4" />
              )}
              Générer PDF
            </Button>
          </div>
        </div>

        {/* Activity */}
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-900 mb-4">
            Activité récente
          </h2>
          <ActivityTimeline activities={activities} />
        </div>
      </div>

      <AssignExpertModal
        isOpen={assignOpen}
        dossierId={id}
        currentExpertId={currentDossier?.expert_id || null}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => fetchDossier(id)}
      />
    </div>
  );
}
